import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Globe, Mail } from "lucide-react";
import { FadeUp } from "@/components/landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-xl bg-white border border-black/15 px-4 py-3 text-sm text-[#0A0A0A] placeholder-black/30 outline-none focus:border-[#0047FF] transition-colors";

const Community = () => {
    const [ticket, setTicket] = useState({ naam: "", email: "", onderwerp: "", bericht: "" });
    const [ticketDone, setTicketDone] = useState(false);
    const [subEmail, setSubEmail] = useState("");
    const [subDone, setSubDone] = useState(false);
    const [subStep, setSubStep] = useState("email");
    const [subCode, setSubCode] = useState("");
    const [subError, setSubError] = useState("");
    const [subSending, setSubSending] = useState(false);
    const [postForm, setPostForm] = useState({ naam: "", email: "", bericht: "" });
    const [postDone, setPostDone] = useState(false);
    const [postError, setPostError] = useState("");
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        axios.get(`${API}/posts/published`).then(({ data }) => setPosts(data)).catch(() => {});
    }, []);

    const submitPost = async (e) => {
        e.preventDefault();
        setPostError("");
        try {
            await axios.post(`${API}/posts`, postForm);
            setPostDone(true);
            toast.success("Bedankt! Je bevinding verschijnt na goedkeuring.");
        } catch (err) {
            const d = err.response?.data?.detail;
            setPostError(typeof d === "string" ? d : "Versturen mislukt. Probeer het later opnieuw.");
        }
    };

    const submitSubscribe = async (e) => {
        e.preventDefault();
        setSubError(""); setSubSending(true);
        try {
            const { data } = await axios.post(`${API}/subscribers`, { email: subEmail, bron: "community" });
            if (data.requires_verification) {
                setSubStep("code");
                toast.success("Code verstuurd — check je e-mail");
            } else {
                setSubDone(true);
                toast.success("Je was al aangemeld — welkom terug!");
            }
        } catch (err) {
            const d = err.response?.data?.detail;
            setSubError(typeof d === "string" ? d : "Aanmelden mislukt.");
        } finally {
            setSubSending(false);
        }
    };

    const verifySubscribe = async (e) => {
        e.preventDefault();
        setSubError(""); setSubSending(true);
        try {
            await axios.post(`${API}/subscribers/verify`, { email: subEmail, code: subCode.trim() });
            setSubDone(true);
            toast.success("Aangemeld — welkom bij de community!");
        } catch (err) {
            const d = err.response?.data?.detail;
            setSubError(typeof d === "string" ? d : "Verifiëren mislukt.");
        } finally {
            setSubSending(false);
        }
    };

    const submitTicket = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/tickets`, ticket);
            setTicketDone(true);
            toast.success("Melding verzonden — we pakken het op!");
        } catch {
            toast.error("Verzenden mislukt.");
        }
    };

    return (
        <div data-testid="community-page" className="min-h-screen bg-[#F9FAFB]">
            <header className="bg-[#0A0A0A] noise-overlay">
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-8 flex items-center justify-between">
                    <div>
                        <a href="/" data-testid="community-logo" aria-label="MeerjarenplanOnline.nl">
                            <img src="/images/logo.png" alt="MeerjarenplanOnline.nl" className="h-7 w-auto" />
                        </a>
                        <p className="text-white/50 text-xs mt-1.5">een service van Club-Fin.nl</p>
                    </div>
                    <a href="/" data-testid="community-back-link" className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-white/60 text-white text-sm font-bold px-5 py-2.5 transition-colors">
                        <ArrowLeft className="w-4 h-4" /> Terug naar home
                    </a>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
                <FadeUp className="text-center">
                    <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#FF6B00] mb-4">Contact</p>
                    <h1 className="font-heading text-[#0A0A0A] font-black tracking-tighter text-4xl sm:text-5xl leading-[1.02] mb-4">
                        Samen sterker<span className="text-[#0047FF]">.</span>
                    </h1>
                    <p className="text-[#0A0A0A]/60 text-base sm:text-lg max-w-2xl leading-relaxed mb-12 mx-auto">
                        Mail ons of meld een probleem — wij reageren binnen één werkdag.
                    </p>
                </FadeUp>

                <div className="max-w-xl mx-auto space-y-8">
                        <FadeUp delay={0.08} className="rounded-2xl bg-[#0A0A0A] noise-overlay p-7">
                            <p className="font-heading text-white font-bold text-xl tracking-tight mb-5">Contact</p>
                            <div className="space-y-3.5">
                                <a data-testid="community-contact-website" href="https://meerjarenplanonline.nl" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors">
                                    <Globe className="w-4 h-4 text-[#FF6B00] shrink-0" />
                                    <span className="text-sm font-bold">MeerjarenplanOnline.nl</span>
                                </a>
                                <a data-testid="community-contact-email" href="mailto:info@club-fin.nl" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors">
                                    <Mail className="w-4 h-4 text-[#FF6B00] shrink-0" />
                                    <span className="text-sm font-bold">info@club-fin.nl</span>
                                </a>
                            </div>
                        </FadeUp>
                        <FadeUp delay={0.12} className="rounded-2xl bg-white border border-black/10 p-7">
                            {subDone ? (
                                <div data-testid="community-subscribe-success" className="text-center py-4">
                                    <CheckCircle2 className="w-10 h-10 text-[#0047FF] mx-auto mb-3" />
                                    <p className="font-heading font-bold text-xl tracking-tight">Je bent aangemeld</p>
                                    <p className="text-[#0A0A0A]/60 text-sm mt-1">Leuk dat je erbij bent — we houden je op de hoogte.</p>
                                </div>
                            ) : (
                                <form data-testid="community-subscribe-form" onSubmit={subStep === "code" ? verifySubscribe : submitSubscribe} className="space-y-3">
                                    <p className="font-heading font-bold text-xl tracking-tight">Meld je aan voor de community</p>
                                    <p className="text-[#0A0A0A]/60 text-sm">Blijf op de hoogte van nieuwe functies, tips en ervaringen van andere verenigingen.</p>
                                    {subStep === "code" ? (
                                        <>
                                            <p data-testid="community-subscribe-code-note" className="text-sm text-[#0A0A0A]/70 bg-[#0047FF]/5 rounded-lg p-3">
                                                We hebben een 6-cijferige code gestuurd naar <span className="font-bold">{subEmail}</span>. Vul die hier in om je aanmelding te bevestigen.
                                            </p>
                                            <input data-testid="community-subscribe-code" inputMode="numeric" autoComplete="one-time-code" value={subCode} onChange={(e) => setSubCode(e.target.value)} placeholder="6-cijferige code" className={`${inputCls} text-center tracking-[0.4em]`} />
                                            {subError && <p data-testid="community-subscribe-error" className="text-[#FF6B00] text-sm font-bold">{subError}</p>}
                                            <button data-testid="community-subscribe-verify" type="submit" disabled={subSending || subCode.trim().length < 6} className="w-full rounded-full bg-[#FF6B00] hover:bg-[#e05e00] text-white font-bold text-sm px-5 py-3 transition-colors disabled:opacity-50">
                                                {subSending ? "Bezig..." : "Bevestig aanmelding"}
                                            </button>
                                            <div className="flex items-center justify-between text-xs">
                                                <button type="button" data-testid="community-subscribe-back" onClick={() => { setSubStep("email"); setSubCode(""); setSubError(""); }} className="text-[#0A0A0A]/40 hover:text-[#0A0A0A] transition-colors">
                                                    Ander e-mailadres
                                                </button>
                                                <button type="button" data-testid="community-subscribe-resend" onClick={submitSubscribe} className="text-[#0A0A0A]/40 hover:text-[#0A0A0A] transition-colors">
                                                    Opnieuw versturen
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <input data-testid="community-subscribe-email" type="email" value={subEmail} onChange={(e) => setSubEmail(e.target.value)} placeholder="E-mailadres *" className={inputCls} />
                                            {subError && <p data-testid="community-subscribe-error" className="text-[#FF6B00] text-sm font-bold">{subError}</p>}
                                            <button data-testid="community-subscribe-submit" type="submit" disabled={subSending} className="w-full rounded-full bg-[#FF6B00] hover:bg-[#e05e00] text-white font-bold text-sm px-5 py-3 transition-colors disabled:opacity-50">
                                                {subSending ? "Bezig..." : "Aanmelden"}
                                            </button>
                                        </>
                                    )}
                                </form>
                            )}
                        </FadeUp>
                        <FadeUp delay={0.15} className="rounded-2xl bg-white border border-black/10 p-7">
                            {postDone ? (
                                <div data-testid="post-success" className="text-center py-4">
                                    <CheckCircle2 className="w-10 h-10 text-[#0047FF] mx-auto mb-3" />
                                    <p className="font-heading font-bold text-xl tracking-tight">Bevinding ontvangen</p>
                                    <p className="text-[#0A0A0A]/60 text-sm mt-1">Na een snelle controle verschijnt hij hieronder op deze pagina.</p>
                                </div>
                            ) : (
                                <form data-testid="post-form" onSubmit={submitPost} className="space-y-3">
                                    <p className="font-heading font-bold text-xl tracking-tight">Deel je bevinding met de community</p>
                                    <p className="text-[#0A0A0A]/60 text-sm">Voor aangemelde community-leden. Je bevinding verschijnt na goedkeuring openbaar op deze pagina.</p>
                                    <input data-testid="post-naam" value={postForm.naam} onChange={(e) => setPostForm({ ...postForm, naam: e.target.value })} placeholder="Je naam *" className={inputCls} />
                                    <input data-testid="post-email" type="email" value={postForm.email} onChange={(e) => setPostForm({ ...postForm, email: e.target.value })} placeholder="E-mailadres waarmee je bent aangemeld *" className={inputCls} />
                                    <textarea data-testid="post-bericht" value={postForm.bericht} onChange={(e) => setPostForm({ ...postForm, bericht: e.target.value })} placeholder="Jouw bevinding, tip of ervaring *" rows={3} className={`${inputCls} resize-none`} />
                                    {postError && <p data-testid="post-error" className="text-[#FF6B00] text-sm font-bold">{postError}</p>}
                                    <button data-testid="post-submit" type="submit" className="w-full rounded-full bg-[#0047FF] hover:bg-[#0036CC] text-white font-bold text-sm px-5 py-3 transition-colors">
                                        Deel bevinding
                                    </button>
                                </form>
                            )}
                        </FadeUp>
                        {posts.length > 0 && (
                            <FadeUp delay={0.18} className="rounded-2xl bg-white border border-black/10 p-7">
                                <p className="font-heading font-bold text-xl tracking-tight mb-4">Bevindingen van de community</p>
                                <div className="space-y-4">
                                    {posts.map((p) => (
                                        <div key={p.id} data-testid={`community-post-${p.id}`} className="border-b border-black/5 last:border-b-0 pb-4 last:pb-0">
                                            <p className="text-sm text-[#0A0A0A]/80 leading-relaxed">{p.bericht}</p>
                                            <p className="mt-1.5 text-xs text-[#0A0A0A]/40 font-bold">
                                                {p.naam} · {new Date(p.created_at).toLocaleDateString("nl-NL", { dateStyle: "medium" })}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </FadeUp>
                        )}
                        <FadeUp delay={0.21} className="rounded-2xl bg-white border border-black/10 p-7">
                            {ticketDone ? (
                                <div data-testid="ticket-success" className="text-center py-4">
                                    <CheckCircle2 className="w-10 h-10 text-[#0047FF] mx-auto mb-3" />
                                    <p className="font-heading font-bold text-xl tracking-tight">Melding ontvangen</p>
                                    <p className="text-[#0A0A0A]/60 text-sm mt-1">We nemen zo snel mogelijk contact op.</p>
                                </div>
                            ) : (
                                <form data-testid="ticket-form" onSubmit={submitTicket} className="space-y-3">
                                    <p className="font-heading font-bold text-xl tracking-tight">Meld een probleem of doe een suggestie</p>
                                    <p className="text-[#0A0A0A]/60 text-sm">Ook als je (nog) geen community-lid bent, kun je hier je opmerkingen insturen.</p>
                                    <input data-testid="ticket-naam" value={ticket.naam} onChange={(e) => setTicket({ ...ticket, naam: e.target.value })} placeholder="Je naam *" className={inputCls} />
                                    <input data-testid="ticket-email" type="email" value={ticket.email} onChange={(e) => setTicket({ ...ticket, email: e.target.value })} placeholder="E-mailadres *" className={inputCls} />
                                    <input data-testid="ticket-onderwerp" value={ticket.onderwerp} onChange={(e) => setTicket({ ...ticket, onderwerp: e.target.value })} placeholder="Onderwerp *" className={inputCls} />
                                    <textarea data-testid="ticket-bericht" value={ticket.bericht} onChange={(e) => setTicket({ ...ticket, bericht: e.target.value })} placeholder="Beschrijf probleem of suggestie *" rows={3} className={`${inputCls} resize-none`} />
                                    <button data-testid="ticket-submit" type="submit" className="w-full rounded-full bg-[#0047FF] hover:bg-[#0036CC] text-white font-bold text-sm px-5 py-3 transition-colors">
                                        Verstuur melding
                                    </button>
                                </form>
                            )}
                        </FadeUp>
                </div>
            </main>
        </div>
    );
};

export default Community;
